import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  RegroupFormModal,
  RegroupViewModal,
} from 'components/modals/modules/regroup-module/RegroupTransactionModal';
import type {
  RegroupFormData,
  RegroupRecord,
  RegroupClosingGlRow,
  RegroupNewGlRow,
} from 'components/modals/modules/regroup-module/RegroupTransactionModal';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useRegroups, useCreateRegroup, useUpdateRegroup, useSubmitRegroup, useDeleteRegroup, useGoldRates } from 'hooks/api';
import { regroupService } from 'api/services/regroupService';
import { loanMappingService } from 'api/services/loanMappingService';
import { GridLoadingSkeleton } from 'components/common';

const emptyFormData: RegroupFormData = {
  mappingId: '', mappingRef: '', customerName: '', customerId: '', addonCustomerId: '',
  loanAccountNumber: '', addonLoanType: '', newAddonLoanAccountNumber: '', newAddonLoanDate: '',
  addonOutstandingPrincipal: '', addonOutstandingInterest: '', requestedAmount: '',
  closingGls: [], newGls: [],
  addonLoanDate: '', cutoffDate: '', cutoffEligible: true, cutoffReason: '',
};

const RegroupTransactionScreen: React.FC = () => {
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useRegroups();
  const { createRegroup } = useCreateRegroup();
  const { updateRegroup } = useUpdateRegroup();
  const { submitRegroup } = useSubmitRegroup();
  const { deleteRegroup } = useDeleteRegroup();
  const { data: goldRates } = useGoldRates();
  const activeGoldRateId = useMemo(() => goldRates.find((r) => r.isActive)?.id ?? '', [goldRates]);

  const regroups = (apiData as unknown as RegroupRecord[]) || [];

  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [, setSelectedRows] = useState<RegroupRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<RegroupRecord | null>(null);
  const [formData, setFormData] = useState<RegroupFormData>(emptyFormData);
  const [statusFilter, setStatusFilter] = useState('');

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; variant: 'default' | 'danger'; loading: boolean; onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', loading: false, onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  const filteredData = useMemo(() =>
    regroups.filter((r) => {
      const matchesSearch = !searchQuery ||
        (r.regroupRef ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.mappingReference ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.customerName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.customerId ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === '' || r.statusCode === statusFilter;
      return matchesSearch && matchesStatus;
    }),
    [regroups, searchQuery, statusFilter]
  );

  const buildPayload = (data: RegroupFormData) => ({
    mappingId: data.mappingId,
    addonOutstandingPrincipal: parseFloat(data.addonOutstandingPrincipal) || 0,
    addonOutstandingInterest: parseFloat(data.addonOutstandingInterest) || 0,
    closingGls: data.closingGls.map((g) => ({
      mappedGlId: g.mappedGlId,
      close: g.close,
      outstandingPrincipal: g.close ? parseFloat(g.outstandingPrincipal) || 0 : 0,
      outstandingInterest: g.close ? parseFloat(g.outstandingInterest) || 0 : 0,
      tokenLossCharge: g.close ? parseFloat(g.tokenLossCharge) || 0 : 0,
    })),
    newAddonCustomerId: data.addonCustomerId,
    newAddonLoanType: data.addonLoanType || undefined,
    newAddonLoanAccountNumber: data.newAddonLoanAccountNumber || undefined,
    newAddonLoanDate: data.newAddonLoanDate || new Date().toISOString().split('T')[0],
    goldRateId: String(activeGoldRateId),
    requestedAmount: parseFloat(data.requestedAmount) || 0,
    newGls: data.newGls.map((g) => ({
      customerId: g.customerId,
      glNumber: g.glNumber,
      netWeight: parseFloat(g.netWeight) || 0,
      loanAmount: parseFloat(g.loanAmount) || 0,
      documentationCharge: parseFloat(g.documentationCharge) || 0,
      loanDate: g.loanDate,
    })),
  });

  const validateForm = (): boolean => {
    if (!formData.mappingId) { showMessage('Search for a lot to regroup first', 'error'); return false; }
    if (!formData.cutoffEligible) { showMessage('This lot is not eligible for Regroup (cut-off date)', 'error'); return false; }
    if (!formData.addonOutstandingPrincipal || parseFloat(formData.addonOutstandingPrincipal) < 0) { showMessage('Add-On outstanding principal is required', 'error'); return false; }
    if (formData.newGls.length === 0) { showMessage('Add at least one new GL', 'error'); return false; }
    if (formData.newGls.some((g) => !g.glNumber || !g.netWeight || !g.customerId)) { showMessage('Complete all new GL rows (customer, GL number, weight)', 'error'); return false; }
    if (!formData.requestedAmount || parseFloat(formData.requestedAmount) <= 0) { showMessage('Requested amount is required', 'error'); return false; }
    return true;
  };

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData({ ...emptyFormData });
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(async (record: RegroupRecord) => {
    try {
      const detailRes = await regroupService.getById(record.id);
      const detail: any = detailRes.data;
      const mappingId = detail?.closingDetail?.mappingId;
      const mappingRes = mappingId ? await loanMappingService.getById(mappingId) : null;
      const mappingDetail: any = mappingRes?.data;

      const closingEntries: any[] = detail?.closingDetail?.glEntries ?? [];
      const closedLookup = new Map<string, any>();
      closingEntries.forEach((e) => closedLookup.set(String(e.mappedGlId), e));

      const closingGls: RegroupClosingGlRow[] = (mappingDetail?.mappedGls ?? [])
        .filter((gl: any) => gl.statusCode === 'OPEN')
        .map((gl: any) => {
          const saved = closedLookup.get(String(gl.id));
          return {
            mappedGlId: String(gl.id),
            glNumber: gl.glNumber ?? '',
            netWeight: gl.netWeight,
            allocationRatio: gl.allocationRatio,
            close: !!saved,
            outstandingPrincipal: saved ? String(saved.outstandingPrincipal ?? '') : '',
            outstandingInterest: saved ? String(saved.outstandingInterest ?? '') : '',
            tokenLossCharge: saved ? String(saved.tokenLossCharge ?? '') : '',
          };
        });

      const newGls: RegroupNewGlRow[] = (detail?.newRequestDetail?.glEntries ?? [])
        .filter((e: any) => !e.sourceMappedGlId)
        .map((e: any) => ({
          customerId: String(e.customerId ?? ''),
          glNumber: e.glNumber ?? '',
          netWeight: String(e.netWeight ?? ''),
          loanAmount: String(e.originalLoanAmount ?? ''),
          documentationCharge: String(e.documentationCharge ?? ''),
          loanDate: e.loanDate ? String(e.loanDate).substring(0, 10) : '',
        }));

      setFormMode('edit');
      setSelectedRecord(record);
      setFormData({
        mappingId: String(mappingId ?? ''),
        mappingRef: record.mappingReference ?? '',
        customerName: record.customerName ?? '',
        customerId: record.customerId ?? '',
        addonCustomerId: String(detail?.newRequestDetail?.addonCustomerId ?? ''),
        loanAccountNumber: detail?.closingDetail?.loanAccountNumber ?? '',
        addonLoanType: detail?.newRequestDetail?.addonLoanType ?? '',
        newAddonLoanAccountNumber: detail?.newRequestDetail?.addonLoanAccountNumber ?? '',
        newAddonLoanDate: detail?.newRequestDetail?.addonLoanDate ? String(detail.newRequestDetail.addonLoanDate).substring(0, 10) : '',
        addonOutstandingPrincipal: String(detail?.closingDetail?.addonOutstandingPrincipal ?? ''),
        addonOutstandingInterest: String(detail?.closingDetail?.addonOutstandingInterest ?? ''),
        requestedAmount: String(detail?.newRequestDetail?.requestedAmount ?? ''),
        closingGls,
        newGls,
        addonLoanDate: '',
        cutoffDate: '',
        cutoffEligible: true,
        cutoffReason: '',
      });
      setFormModalOpen(true);
    } catch {
      showMessage('Failed to load regroup details for editing', 'error');
    }
  }, [showMessage]);

  const handleView = useCallback((record: RegroupRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const runSave = (submitAfter: boolean) => {
    if (!validateForm()) return;
    const action = formMode === 'create' ? 'Create' : 'Save';
    setConfirmDialog({
      open: true,
      title: submitAfter ? 'Submit Regroup' : `${action} Regroup`,
      message: submitAfter ? 'Submit this Regroup transaction for approval?' : `${action} this Regroup transaction as Draft?`,
      variant: 'default',
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          let recordId: string | undefined;
          if (formMode === 'edit' && selectedRecord) {
            await updateRegroup(selectedRecord.id, { ...buildPayload(formData), rowVersion: selectedRecord.rowVersion ?? 0 });
            recordId = selectedRecord.id;
          } else {
            const created = await createRegroup(buildPayload(formData));
            recordId = String(created?.id);
          }
          if (submitAfter && recordId) await submitRegroup(recordId);
          await refetch();
          showMessage(submitAfter ? 'Regroup submitted for approval' : `Regroup ${formMode === 'create' ? 'created' : 'updated'}`, 'success');
          setFormModalOpen(false);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
        } catch (err: any) {
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          showMessage(err?.response?.data?.message || 'Failed to save Regroup', 'error');
        }
      },
    });
  };

  const handleDelete = useCallback((record: RegroupRecord) => {
    setConfirmDialog({
      open: true, title: 'Delete Regroup', message: `Delete Regroup ${record.regroupRef}?`, variant: 'danger', loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await deleteRegroup(record.id);
          await refetch();
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          showMessage('Regroup deleted', 'success');
        } catch (err: any) {
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          showMessage(err?.response?.data?.message || 'Failed to delete Regroup', 'error');
        }
      },
    });
  }, [showMessage, deleteRegroup, refetch]);

  const handleSubmitRow = useCallback((record: RegroupRecord) => {
    setConfirmDialog({
      open: true, title: 'Submit for Approval',
      message: `Submit Regroup ${record.regroupRef} (Net Cash Flow: ${formatCurrency(record.netCashFlow)}) for approval?`,
      variant: 'default', loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await submitRegroup(record.id);
          await refetch();
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          showMessage('Regroup submitted for approval', 'success');
        } catch (err: any) {
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          showMessage(err?.response?.data?.message || 'Failed to submit Regroup', 'error');
        }
      },
    });
  }, [showMessage, submitRegroup, refetch]);

  const getRowActions = useCallback((row: RegroupRecord): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(row) },
    ];
    if (row.statusCode === 'DRAFT' || row.statusCode === 'RETURNED') {
      actions.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(row) });
      actions.push({ id: 'submit', label: row.statusCode === 'RETURNED' ? 'Resubmit for Approval' : 'Submit for Approval', icon: <IconSystem.check size={18} />, onClick: () => handleSubmitRow(row) });
    }
    if (row.statusCode === 'DRAFT') {
      actions.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(row), variant: 'danger' as const });
    }
    return actions;
  }, [handleView, handleEdit, handleSubmitRow, handleDelete]);

  const tableColumns: SyutGridColumn<RegroupRecord>[] = useMemo(() => [
    { id: 'regroupRef', header: 'Regroup Ref', accessorKey: 'regroupRef', size: 150 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 150 },
    { id: 'mappingReference', header: 'Existing Mapping', accessorKey: 'mappingReference', size: 140 },
    {
      id: 'glsSplit', header: 'GLs Closed / Retained', size: 150,
      cell: ({ row }: any) => <span>{row.original.glsClosed ?? 0} closed / {row.original.glsRetained ?? 0} retained</span>,
    },
    {
      id: 'newAddonAmount', header: 'New Add-On', accessorKey: 'newAddonAmount', size: 130,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(getValue() ?? 0)}</span>,
    },
    {
      id: 'netCashFlow', header: 'Net Cash Flow', accessorKey: 'netCashFlow', size: 140,
      cell: ({ getValue }: any) => {
        const val = getValue() ?? 0;
        return <span style={{ fontWeight: 600, color: val >= 0 ? '#16a34a' : '#dc2626' }}>{val >= 0 ? '+' : ''}{formatCurrency(val)}</span>;
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 150,
      cell: ({ getValue }: any) => <span style={getStatusBadgeStyle(getValue() ?? '')}>{(getValue() ?? '').replace(/_/g, ' ')}</span>,
    },
    { id: 'submittedAt', header: 'Submitted On', accessorKey: 'submittedAt', size: 120, cell: ({ getValue }: any) => (getValue() ? formatDate(getValue()) : '-') },
  ], []);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'New Regroup', onClick: handleAddNew, tooltip: 'Create New Regroup', variant: 'icon', visible: true },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Regroup Transactions"
        icon={<IconSystem.refresh size={20} />}
        description="Close an existing Add-On Loan and regroup GLs into a new lot with a fresh Add-On Loan."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search regroups...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && regroups.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load regroup transactions</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && regroups.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
          <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                enableRowSelection
                enablePagination
                enableColumnFilters={false}
                rowActions={getRowActions}
                frozenRightColumns={['actions']}
                onSelectionChange={setSelectedRows}
                emptyMessage="No regroup transactions found"
              />
            </div>
          </div>
        )}

        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'RETURNED', label: 'Returned' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => setStatusFilter('')}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>

      <RegroupFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={() => runSave(false)}
        onSubmit={() => runSave(true)}
        onClose={() => setFormModalOpen(false)}
        isProcessing={confirmDialog.loading}
      />

      <RegroupViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default RegroupTransactionScreen;
